import React, { useContext } from 'react';
import { useParams } from 'react-router-dom';
import { TabsProps } from 'antd';
import queryString from 'query-string';

import IBTooltip from '@totem/components/common/helpTooltips/IBTooltip';
import DevicesContainer, {
  containerConfiguration,
} from '@totem/components/devices/devicesContainer/DevicesContainer';
import AliasesTable from '@totem/components/software/software_package/aliasesTable';
import DeviceTable from '@totem/components/software/software_package/deviceTable';
import KeysPane from '@totem/components/software/software_package/keysPane';
import SoftwareContainer from '@totem/components/software/software_package/packageContainer';
import PackageEdit from '@totem/components/software/software_package/packageEdit';
import PackageHeader from '@totem/components/software/software_package/packageHeader';
import PackageTitle from '@totem/components/software/software_package/packageTitle';
import PackageVersions from '@totem/components/software/software_package/packageVersions';
import PackageVulnerabilities from '@totem/components/software/software_package/packageVulnerabilities';
import IBCard from '@totem/components/ui_components/antDesignOverrides/IBCard';
import IBTabs from '@totem/components/ui_components/antDesignOverrides/IBTabs';
import ContentLayout from '@totem/components/ui_components/page/ContentLayout';
import InputContainer from '@totem/components/ui_components/reportSettings/InputContainer';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { Breadcrumb } from '@totem/types/breadcrumb';
import { getCalculatedBreadcrumb } from '@totem/utilities/breadcrumbs';
import { isNotNull } from '@totem/utilities/common';
import { DEVICES_PAGINATION_ENDPOINT } from '@totem/utilities/endpoints';

const styles = {
  totals: {
    fontSize: '16px',
    marginRight: '20px',
  },
  tabContainer: {},
};

const PackagePage = () => {
  const { id } = useParams();
  const { userProfile } = useContext(UserProfileContext);
  const insternalSystemAdministrator =
    permissions.internalSystemAdministrator.hasPermissions(userProfile);

  const getBreadcrumbs = (breadcrumbsIn: Breadcrumb) => {
    const breadcrumbs = [breadcrumbsIn];

    const parsed = queryString.parse(location.search);

    if (isNotNull(parsed.from)) {
      if (typeof parsed.from === 'string') {
        const fromBreadcrumb = parsed.from;
        const newBreadcrumb = getCalculatedBreadcrumb(fromBreadcrumb);

        if (newBreadcrumb !== null) {
          breadcrumbs.push(newBreadcrumb);
        }
      }
    }

    breadcrumbs.push({
      label: `Software Package`,
    });

    return breadcrumbs;
  };

  const getTabItems = () => {
    const tabItems: TabsProps['items'] = [
      {
        key: 'versions',
        label: 'Versions',
        children: <PackageVersions />,
      },
      {
        key: 'vulnerabilities',
        label: 'Vulnerabilities',
        children: <PackageVulnerabilities />,
      },
    ];

    if (insternalSystemAdministrator) {
      tabItems.push({
        key: 'aliases',
        label: (
          <IBTooltip content={permissions.internalSystemAdministrator.tooltip}>
            {'Aliases'}
          </IBTooltip>
        ),
        children: <AliasesTable />,
      });

      tabItems.push({
        key: 'keys',
        label: (
          <IBTooltip content={permissions.internalSystemAdministrator.tooltip}>
            {'Keys'}
          </IBTooltip>
        ),
        children: <KeysPane />,
      });
    }

    tabItems.push({
      key: 'devices',
      label: 'Devices',
      children: (
        <InputContainer
          filterConfig={containerConfiguration}
          defaultSort={{
            field: 'device.displayName',
            direction: 'ascend',
          }}
          componentKey={'software.package.devices'}
        >
          <DevicesContainer
            deviceEndPoint={`${DEVICES_PAGINATION_ENDPOINT}/software/${id}`}
          >
            <DeviceTable />
          </DevicesContainer>
        </InputContainer>
      ),
    });

    return tabItems;
  };

  return (
    <SoftwareContainer packageName={id}>
      <ContentLayout
        breadcrumbs={(breadcrumbs) => getBreadcrumbs(breadcrumbs[0])}
      >
        <IBCard isCovered title={<PackageTitle />} extra={<PackageEdit />}>
          <PackageHeader />
          <IBTabs
            defaultActiveKey="versions"
            style={styles.tabContainer}
            items={getTabItems()}
          />
        </IBCard>
      </ContentLayout>
    </SoftwareContainer>
  );
};

export default PackagePage;
